// Privacy guard — canon §4 (rules 1–9). Phase 1 implementation.
// Hard contract: PII and precise geo MUST NEVER appear in an analytics payload.
//
// Defense in depth:
//  1) KEY-based: forbidden keys (phone, email, name, address, city, delivery_location,
//     lat/lng, comment, …) are rejected at any nesting depth. This is the guarantee
//     for geo — precise locations are key-controlled (canon rule 9), so `delivery_location`
//     etc. simply can never appear here; they go only to POST /v1/leads.
//  2) VALUE-based: phone-like string values are rejected anywhere (a phone smuggled
//     into a generic field like properties.value).
//
// Care (canon Phase 1): do NOT false-positive on legit values — plant size "200–250 см",
// money "70000", step numbers. Two-sided tests live in privacy.test.ts.

export class PIIInAnalyticsError extends Error {
  constructor(
    public readonly field: string,
    public readonly kind: 'forbidden_key' | 'phone_value',
  ) {
    super(`PII detected in analytics payload at "${field}" (${kind})`);
    this.name = 'PIIInAnalyticsError';
  }
}

/**
 * Forbidden key tokens (canon rule 1, 8, 9). A key is rejected if any of its tokens
 * (split on separators + camelCase) is in this set. `region` is intentionally allowed
 * (coarse region is OK per rule 9); `location`/`city`/`district` are NOT (precise geo).
 */
export const PII_FORBIDDEN_KEYS = [
  'phone', 'phones', 'telephone', 'tel', 'msisdn', 'whatsapp', 'viber',
  'email', 'mail',
  'name', 'firstname', 'lastname', 'fullname', 'surname', 'fio', 'patronymic',
  'address', 'addr', 'street', 'house', 'apartment', 'flat',
  'city', 'town', 'village', 'settlement', 'district', 'location',
  'geo', 'lat', 'lon', 'lng', 'latitude', 'longitude', 'coords', 'coordinates',
  'comment',
] as const;

export type PiiForbiddenKey = (typeof PII_FORBIDDEN_KEYS)[number];

const FORBIDDEN = new Set<string>(PII_FORBIDDEN_KEYS);

/** Multi-word keys that survive tokenization without a separated token match. */
const FORBIDDEN_JOINED = ['deliverylocation', 'deliveryaddress', 'phonenumber', 'geolocation'];

function tokenize(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/u)
    .filter(Boolean);
}

function isForbiddenKey(key: string): boolean {
  const joined = key.toLowerCase().replace(/[^a-z0-9]+/gu, '');
  if (FORBIDDEN_JOINED.some((f) => joined.includes(f))) return true;
  return tokenize(key).some((t) => FORBIDDEN.has(t));
}

const RU_PHONE_RE = /(?:\+?7|8)[\s\-().]*\d{3}[\s\-().]*\d{3}[\s\-().]*\d{2}[\s\-().]*\d{2}/;

/** A string "looks like a phone" if it matches the RU phone shape, OR the WHOLE
 *  string is digits + phone-style separators yielding 10–15 digits with a trunk/country
 *  prefix. The "whole string" rule avoids false positives on ids like `70000_150000`
 *  or money/sizes (canon Phase 1: do not flag legit numeric values). */
export function looksLikePhone(value: string): boolean {
  if (RU_PHONE_RE.test(value)) return true;
  if (!/^[\s+\-().\d]+$/u.test(value)) return false; // contains non-phone chars (e.g. "_") → not a phone
  const digits = value.replace(/\D/gu, '');
  return digits.length >= 10 && digits.length <= 15 && /^[78]/u.test(digits);
}

function scan(value: unknown, path: string): void {
  if (Array.isArray(value)) {
    value.forEach((v, i) => scan(v, `${path}[${i}]`));
    return;
  }
  if (value !== null && typeof value === 'object') {
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (isForbiddenKey(key)) throw new PIIInAnalyticsError(`${path}.${key}`, 'forbidden_key');
      scan(val, `${path}.${key}`);
    }
    return;
  }
  if (typeof value === 'string' && looksLikePhone(value)) {
    throw new PIIInAnalyticsError(path, 'phone_value');
  }
}

/**
 * Throws PIIInAnalyticsError if the analytics payload contains PII or precise geo.
 * Call on the user-controlled parts of an event (properties + context), NOT the
 * validated envelope (whose `event_name` legitimately contains the token "name").
 */
export function assertNoPIIInAnalyticsPayload(payload: unknown): void {
  scan(payload, '$');
}

/** Non-throwing convenience wrapper. */
export function containsPII(payload: unknown): boolean {
  try {
    assertNoPIIInAnalyticsPayload(payload);
    return false;
  } catch (err) {
    if (err instanceof PIIInAnalyticsError) return true;
    throw err;
  }
}
